import { isHiddenGame, ONLINE_RESUME_OFFER_MS, type GameId } from "@bing/shared";
import { loadOnlineGame } from "./online-resume";

/**
 * STRAIGHT BACK TO THE HELD TABLE (card-a5 item 1, operator 2026-10-11).
 *
 * «Reopening the app while the seat is still held goes straight back to the table.» A public seat is
 * held from the moment its player leaves the game in progress without «خروج» — a closed app, a lost
 * socket, a crash — for as long as the server keeps its reservation. On app open, through any entry
 * (the menu button, a deep link, a reload), the app goes to that table before any other screen,
 * without a tap: `main.tsx`'s bootstrap asks {@link straightBackTarget} and, when it names a table,
 * enters it as a `rejoin` (`client.reconnect` with this device's ticket — the door that walks past
 * the room lock and never runs `onAuth`, so it passes maintenance, the channel gate, a ban and
 * «بزودی» exactly as a resume does: CLAUDE.md «THE PLATFORM GATES STOP NEW GAMES ONLY»).
 *
 * TWO SOURCES, BOTH THIS DEVICE'S OWN:
 *  · the «ادامه بازی» ticket (`online-resume.ts`) when it names a PUBLIC table that has DEALT — the
 *    seat's reservation lasts the whole match there (the grace and the manual reservation after it);
 *  · a PUBLIC WAITING ROOM's seat ({@link rememberWaitingSeat}) — held 120 s by the server
 *    (`PUBLIC_PRESTART_RECONNECT_WINDOW_SECONDS`). Kept under its OWN key, never on the strip: a public
 *    waiting room is not «a game in progress», and the ticket's own rule keeps those off the strip.
 *    It is only ever offered inside that window ({@link PUBLIC_WAITING_HOLD_MS}), measured from the
 *    last moment the waiting room was seen alive — after it, the server has let the seat go.
 *
 * ⚠️ SAME DEVICE ONLY. The seat's reconnection token is the only key to a locked table, and it lives
 * on the device that sat there (item 2 keeps it out of every URL and log; no route hands it to
 * another device). A device without the ticket cannot return the player, and then nothing is charged
 * either — the brief's «where the hold exists but the return cannot happen, the player is not
 * charged». The server's refusal of other public games still applies there (it is cross-instance).
 *
 * A return that FAILS is silent (`OnlineGame`): the app simply opens where it would have opened.
 */

/** The public waiting-room seat's own key — separate from the «ادامه بازی» ticket on purpose. */
const WAITING_KEY = "bing:public-waiting-seat";

/**
 * How long a public waiting-room seat is worth returning to — the server's pre-start hold for a public
 * table (`PUBLIC_PRESTART_RECONNECT_WINDOW_SECONDS` = 120 s, `apps/game-server/src/rooms/online-room.ts`).
 * Measured from the LAST lobby frame this device received, which is no later than the drop, so the
 * reservation (minted at the server's detection of the drop) lives at least this long after it.
 */
export const PUBLIC_WAITING_HOLD_MS = 120_000;

export interface WaitingSeat {
  roomId: string;
  game: GameId;
  token: string;
  /** Epoch ms of the last lobby frame — the last moment the seat was demonstrably ours and live. */
  at: number;
}

/** We are sitting in a PUBLIC waiting room: remember the seat (re-stamped on every lobby frame). */
export function rememberWaitingSeat(seat: Omit<WaitingSeat, "at">, now: number = Date.now()): void {
  try {
    localStorage.setItem(WAITING_KEY, JSON.stringify({ ...seat, at: now }));
  } catch {
    // private mode / quota — the straight back simply won't happen; nothing else breaks.
  }
}

/** Forget it (the table dealt, closed or expired; we left it; a return to it failed). With
 *  `roomId`, only when it names that room — never a NEWER table's seat. */
export function forgetWaitingSeat(roomId?: string): void {
  try {
    if (roomId) {
      const raw = localStorage.getItem(WAITING_KEY);
      const seat = raw ? (JSON.parse(raw) as Partial<WaitingSeat>) : null;
      if (seat && seat.roomId !== roomId) return;
    }
    localStorage.removeItem(WAITING_KEY);
  } catch {
    // ignore
  }
}

/** The remembered waiting-room seat while the server still holds it, else null (a stale one is dropped). */
export function loadWaitingSeat(now: number = Date.now()): WaitingSeat | null {
  try {
    const raw = localStorage.getItem(WAITING_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<WaitingSeat>;
    if (
      typeof s.roomId !== "string" ||
      typeof s.game !== "string" ||
      typeof s.token !== "string" ||
      typeof s.at !== "number"
    ) {
      localStorage.removeItem(WAITING_KEY);
      return null;
    }
    if (now - s.at >= PUBLIC_WAITING_HOLD_MS || s.at > now + PUBLIC_WAITING_HOLD_MS) {
      localStorage.removeItem(WAITING_KEY);
      return null;
    }
    return { roomId: s.roomId, game: s.game as GameId, token: s.token, at: s.at };
  } catch {
    return null;
  }
}

/**
 * THE PUBLIC SEAT THIS PHONE JUST LEFT (card-98 item 3b, operator 2026-10-27): «the app keeps the seat
 * it just left; the refusal screen offers «برگشت به بازی»». A waiting room left as it dealt can be
 * held by the server for the whole match (a phone that never showed the cards had no ticket for it),
 * so on a deliberate exit the waiting room's seat is KEPT here rather than discarded — and only the
 * «بازی عمومی» refusal reads it: the app never goes straight back to it on open. Same phone only.
 * Dropped when a way back to it fails for good, replaced by the next seat left; kept no longer than
 * a returning player is offered any dead table ({@link ONLINE_RESUME_OFFER_MS}).
 */
const LEFT_KEY = "bing:public-left-seat";

/** The waiting-room seat was left in-app: keep it as the seat just left (with `roomId`, only that
 *  room's). Replaces {@link forgetWaitingSeat} on the deliberate-exit paths. */
export function leaveWaitingSeat(roomId?: string, now: number = Date.now()): void {
  try {
    const raw = localStorage.getItem(WAITING_KEY);
    const seat = raw ? (JSON.parse(raw) as Partial<WaitingSeat>) : null;
    if (roomId && seat && seat.roomId !== roomId) return;
    if (
      seat &&
      typeof seat.roomId === "string" &&
      typeof seat.game === "string" &&
      typeof seat.token === "string"
    ) {
      const left = { roomId: seat.roomId, game: seat.game, token: seat.token, at: now };
      localStorage.setItem(LEFT_KEY, JSON.stringify(left));
    }
  } catch {
    // private mode / quota — no way back is kept; the exit itself is unaffected.
  }
  forgetWaitingSeat(roomId);
}

/** The public seat this phone just left, while it may still be held — else null (a stale one is dropped). */
export function loadLeftSeat(now: number = Date.now()): WaitingSeat | null {
  try {
    const raw = localStorage.getItem(LEFT_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<WaitingSeat>;
    const valid =
      typeof s.roomId === "string" &&
      typeof s.game === "string" &&
      typeof s.token === "string" &&
      typeof s.at === "number" &&
      now - s.at < ONLINE_RESUME_OFFER_MS &&
      s.at <= now + PUBLIC_WAITING_HOLD_MS;
    if (!valid) {
      localStorage.removeItem(LEFT_KEY);
      return null;
    }
    return { roomId: s.roomId!, game: s.game as GameId, token: s.token!, at: s.at! };
  } catch {
    return null;
  }
}

/** Forget the seat just left (a way back to it failed for good). With `roomId`, only that room's. */
export function forgetLeftSeat(roomId?: string): void {
  try {
    if (roomId) {
      const raw = localStorage.getItem(LEFT_KEY);
      const seat = raw ? (JSON.parse(raw) as Partial<WaitingSeat>) : null;
      if (seat && seat.roomId !== roomId) return;
    }
    localStorage.removeItem(LEFT_KEY);
  } catch {
    // ignore
  }
}

/** The «بازی عمومی» refusal's way back: the held table, else the seat this phone just left. */
export function refusalWayBack(now: number = Date.now()): StraightBack | null {
  const held = straightBackTarget(now);
  if (held) return held;
  const left = loadLeftSeat(now);
  return left && !isHiddenGame(left.game)
    ? { game: left.game, roomId: left.roomId, token: left.token, waiting: false }
    : null;
}

/** Where the app goes before any other screen. `waiting` says which source named it. */
export interface StraightBack {
  game: GameId;
  roomId: string;
  token: string;
  /** True for a public waiting room (nothing dealt yet); false for a public table in progress. */
  waiting: boolean;
}

/**
 * The held table this device can go straight back to, or null. A dealt public table wins over a
 * waiting room (one player is in one public game at a time; the dealt one is what is at stake). A
 * game hidden from players is never offered back (the lobby strip's and the crash door's rule).
 */
export function straightBackTarget(now: number = Date.now()): StraightBack | null {
  const ticket = loadOnlineGame();
  if (ticket && ticket.public === true && ticket.pending !== true && !isHiddenGame(ticket.game)) {
    return { game: ticket.game, roomId: ticket.roomId, token: ticket.token, waiting: false };
  }
  const seat = loadWaitingSeat(now);
  if (seat && !isHiddenGame(seat.game)) {
    return { game: seat.game, roomId: seat.roomId, token: seat.token, waiting: true };
  }
  return null;
}
